import { log } from '../utils/logging.js';
import { LoopOnce } from 'three';
import { shouldSkipIntro } from '../animation/animationMixer.js';

const OPEN_NAME = 'open_hatch_trigger';
const CLOSE_NAME = 'close_hatch_trigger';
const SKIP_INTRO = shouldSkipIntro('market') || false;

const byName = (actions = {}, name = '') => actions[name.toLowerCase()] || null;

const getTargets = action => {
  if (!action?.getClip) return new Set();
  const clip = action.getClip();
  const targets = new Set();
  (clip?.tracks || []).forEach(track => {
    const base = track?.name?.split?.('.')[0];
    if (base) targets.add(base.toLowerCase());
  });
  return targets;
};

const armAction = action => {
  if (!action) return;
  action.enabled = true;
  action.paused = true;
  action.time = 0;
  action.weight = 1;
};

const actionSnapshot = (label, action) => {
  if (!action) {
    log(`[hatch:snap] ${label} → null`);
    return;
  }
  log(`[hatch:snap] ${label}`, {
    time: action.time,
    paused: action.paused,
    enabled: action.enabled,
    weight: action.weight,
    isRunning: action.isRunning?.(),
    duration: action.getClip?.()?.duration
  });
};

const playAction = (action, otherAction = null) => {
  if (!action) return;
  const mixer = action.getMixer?.();
  if (otherAction) {
    otherAction.stop();
    otherAction.enabled = false;
    otherAction.paused = true;
    otherAction.weight = 0;
    otherAction.setEffectiveWeight?.(0);
  }
  action.reset();
  action.enabled = true;
  action.paused = false;
  action.time = 0;
  action.setEffectiveWeight?.(1);
  action.weight = 1;
  action.setEffectiveTimeScale?.(1);
  action.setLoop(LoopOnce, 1);
  action.clampWhenFinished = true;
  action.play();
  if (mixer) {
    const onFinished = event => {
      if (event?.action !== action) return;
      action.paused = true;
      action.enabled = true;
      action.time = action.getClip()?.duration ?? action.time;
      mixer.removeEventListener('finished', onFinished);
    };
    mixer.addEventListener('finished', onFinished);
  }
};

export const initHatchTriggers = (
  animationController,
  {
    autoOpen = false,
    autoOpenFrame = null,
    autoClose = false,
    autoCloseFrame = null,
    fps = 24,
    skipIntro = SKIP_INTRO
  } = {}
) => {
  log('[hatch:init] ─────────────────────────────────────');
  log('[hatch:init] called with', {
    autoOpen,
    autoOpenFrame,
    autoClose,
    autoCloseFrame,
    fps,
    skipIntro
  });

  const actions = animationController?.actions || {};
  const openAction = byName(actions, OPEN_NAME);
  const closeAction = byName(actions, CLOSE_NAME);

  log('[hatch:init] actions found', {
    openAction: !!openAction,
    closeAction: !!closeAction,
    allActionKeys: Object.keys(actions)
  });

  let hatchState = 'closed';
  let autoOpenTimer = null;
  let autoCloseTimer = null;

  if (!openAction && !closeAction) {
    log('[hatch:init] no hatch actions found — bailing out');
    return () => {};
  }

  const hatchTargets = new Set([...getTargets(openAction), ...getTargets(closeAction)]);
  log('[hatch:init] hatchTargets', Array.from(hatchTargets));

  // Stop any other action that touches the same nodes as the hatch clips.
  Object.values(actions).forEach(act => {
    if (!act || act === openAction || act === closeAction) return;
    const targets = getTargets(act);
    const overlaps = [...targets].some(name => hatchTargets.has(name));
    if (overlaps) {
      log('[hatch:init] stopping overlapping action:', act.getClip().name);
      act.stop();
      act.enabled = false;
      act.paused = true;
      act.setEffectiveWeight?.(0);
      act.weight = 0;
    }
  });

  // Ensure openAction is completely stopped and reset before we begin.
  if (openAction) {
    log('[hatch:init] hard-stopping openAction before arm');
    openAction.stop();
    openAction.enabled = false;
    openAction.paused = true;
    openAction.time = 0;
    openAction.weight = 0;
  }

  armAction(openAction);
  armAction(closeAction);
  actionSnapshot('after initial arm — openAction', openAction);
  actionSnapshot('after initial arm — closeAction', closeAction);

  // ── Branch: closeAction exists ──────────────────────────────────────────────
  if (closeAction) {
    log('[hatch:branch] closeAction exists');
    hatchState = 'closed';
    armAction(closeAction);
    if (openAction) armAction(openAction);

    if (skipIntro) {
      log('[hatch:skip] skipIntro=true → closing briefly then opening after 400ms');
      hatchState = 'closed';
      autoOpen = false;
    } else {
      log('[hatch:no-skip] scheduling force-close hack in 100ms');
      setTimeout(() => {
        log('[hatch:hack] Force-closing hatch on Frame 2 hack');
        actionSnapshot('before force-close — closeAction', closeAction);
        actionSnapshot('before force-close — openAction', openAction);
        playAction(closeAction, openAction);
        actionSnapshot('after force-close — closeAction', closeAction);
      }, 100);
    }

    // ── Branch: only openAction exists ─────────────────────────────────────────
  } else if (openAction) {
    log('[hatch:branch] only openAction exists');

    if (skipIntro) {
      log('[hatch:skip] only openAction + skipIntro → hatch stays open');
      hatchState = 'open';
      autoOpen = false;
    } else {
      log('[hatch:no-skip] only openAction → arming at frame 0 (closed)');
      openAction.reset();
      openAction.time = 0;
      openAction.enabled = true;
      openAction.paused = true;
      openAction.play();
      const mixer = openAction.getMixer?.();
      if (mixer) mixer.update(0);
      hatchState = 'closed';
      actionSnapshot('after arm at frame 0 — openAction', openAction);
    }
  }

  log('[hatch:init] hatchState after branch:', hatchState, '| autoOpen:', autoOpen);

  // ── autoOpen ────────────────────────────────────────────────────────────────
  if (autoOpen && !skipIntro && openAction) {
    const delayMs = Number.isFinite(autoOpenFrame)
      ? Math.max(0, (autoOpenFrame / (fps || 24)) * 1000)
      : 0;
    log('[hatch:autoOpen] scheduling autoOpen in', delayMs, 'ms (frame', autoOpenFrame, ')');
    const trigger = () => {
      log('[hatch:autoOpen] trigger fired → playing openAction');
      actionSnapshot('autoOpen trigger — openAction before', openAction);
      hatchState = 'open';
      playAction(openAction, closeAction);
      actionSnapshot('autoOpen trigger — openAction after', openAction);
    };
    if (delayMs > 0) {
      autoOpenTimer = setTimeout(trigger, delayMs);
    } else {
      trigger();
    }
  } else {
    log('[hatch:autoOpen] skipped', { autoOpen, skipIntro, hasOpenAction: !!openAction });
  }

  // ── autoClose ───────────────────────────────────────────────────────────────
  if (autoClose && !skipIntro && closeAction) {
    const delayMs = Number.isFinite(autoCloseFrame)
      ? Math.max(0, (autoCloseFrame / (fps || 24)) * 1000)
      : 0;
    log('[hatch:autoClose] scheduling autoClose in', delayMs, 'ms (frame', autoCloseFrame, ')');
    if (delayMs > 0) {
      const trigger = () => {
        log('[hatch:autoClose] trigger fired → playing closeAction');
        hatchState = 'closed';
        playAction(closeAction, openAction);
      };
      autoCloseTimer = setTimeout(trigger, delayMs);
    }
  } else {
    log('[hatch:autoClose] skipped', { autoClose, skipIntro, hasCloseAction: !!closeAction });
  }

  // ── Event listeners ─────────────────────────────────────────────────────────
  const onOpen = () => {
    log('[hatch:event] hatch:open received, current state:', hatchState);
    if (hatchState === 'open') {
      log('[hatch:event] already open — ignoring');
      return;
    }
    hatchState = 'open';
    playAction(openAction, closeAction);
  };
  const onClose = () => {
    log('[hatch:event] hatch:close received, current state:', hatchState);
    if (hatchState === 'closed') {
      log('[hatch:event] already closed — ignoring');
      return;
    }
    hatchState = 'closed';
    playAction(closeAction, openAction);
  };

  window.addEventListener('hatch:open', onOpen);
  window.addEventListener('hatch:close', onClose);

  log('[hatch:init] ✓ done, hatchState:', hatchState);

  return () => {
    log('[hatch:dispose] cleaning up hatch triggers');
    window.removeEventListener('hatch:open', onOpen);
    window.removeEventListener('hatch:close', onClose);
    if (autoOpenTimer) {
      clearTimeout(autoOpenTimer);
      autoOpenTimer = null;
    }
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
    armAction(openAction);
    armAction(closeAction);
  };
};
